import { Injectable } from "@angular/core";
import  moment from "moment";
import { BehaviorSubject } from "rxjs";
import { Global } from '../_constants/global.variables';
import { AlertController, ToastController, IonicSafeString } from "@ionic/angular";

export interface applicationSharedNotification {
	type: string;
	title?: string;
	message: string;
	override?: any;
}
@Injectable({
	providedIn: 'root',
})
export class UtilityService {
	public timeZoneOffsetHoursFromUTC = new Date().getTimezoneOffset() / 60;
	private readonly videoDiagTrailKey = 'iopsVideoDiagnosticsTrail';
	private readonly videoDiagMaxEntries = 1500;
	private tapNavigationBlockedUntilMs = 0;
	private ghostTapShieldUntilMs = 0;
	private ghostTapShieldAttached = false;
	private readonly ghostTapShieldEventTypes: string[] = [
		"touchstart",
		"touchend",
		"pointerdown",
		"pointerup",
		"mousedown",
		"mouseup",
		"click",
	];
	private readonly ghostTapShieldHandler = (event: Event) => {
		if (Date.now() > this.ghostTapShieldUntilMs) {
			this.detachGhostTapShieldIfExpired();
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		(event as any).stopImmediatePropagation?.();
	};
	public homeRoute$: BehaviorSubject<string> = new BehaviorSubject<string>(
		'layout/dashboard-demo'
	);
	public titleMenuChange$: BehaviorSubject<string> =
		new BehaviorSubject<string>(Global.CurrentMenuItem);
    public overviewRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('overview');
    public alarmsRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('alarms');
    public tagsRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('tags');
    public videosRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('videos');
    public moreRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('more');
    public goBackRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public menuOverviewRoute$: BehaviorSubject<string> = new BehaviorSubject<string>('site-summary');
	public assetTypeGroupChange$: BehaviorSubject<number | null | undefined> = new BehaviorSubject<number | null | undefined>(Global.User?.currentUser?.SelectedAssetTypeGroupId);
	public modalSearchClicked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public goBackRouteAndScrollToPosition$ = new BehaviorSubject<{ url: string; scroll?: number, systemId: number }>({ url: '', scroll: 0, systemId: 0 });

	constructor(
		private mobileToastr: ToastController,
		private mobileAlertify: AlertController
	) {
	}

	updateCurrentMenuItem(title: string) {
		console.log('updateCurrentMenuItem invoked. title = ' + title);
		Global.CurrentMenuItem = title;
		this.titleMenuChange$.next(title);
	}

	logVideoDiagnostic(stage: string, details?: any, source: string = 'shared.video.diagnostics'): void {
		if (typeof window === "undefined" || typeof localStorage === "undefined") {
			return;
		}

		const entry = {
			ts: new Date().toISOString(),
			source,
			stage,
			route: `${window.location.pathname}${window.location.search}${window.location.hash}`,
			details: this.toDebugValue(details),
		};

		console.log('[VideoDiagShared]', entry);
		try {
			const existing = JSON.parse(localStorage.getItem(this.videoDiagTrailKey) || '[]');
			existing.push(entry);
			if (existing.length > this.videoDiagMaxEntries) {
				existing.splice(0, existing.length - this.videoDiagMaxEntries);
			}
			localStorage.setItem(this.videoDiagTrailKey, JSON.stringify(existing));
		} catch {
			return;
		}
	}

	/**
	 * Guards route-navigation taps against iOS "ghost" re-taps across page transitions.
	 * Returns false if a tap-triggered navigation should be ignored right now.
	 */
	beginTapNavigation(guardWindowMs: number = 700): boolean {
		const now = Date.now();
		if (now < this.tapNavigationBlockedUntilMs) {
			this.armGhostTapShield(this.tapNavigationBlockedUntilMs - now);
			return false;
		}

		const safeWindowMs = Math.max(0, guardWindowMs);
		this.tapNavigationBlockedUntilMs = now + safeWindowMs;
		this.armGhostTapShield(safeWindowMs);
		return true;
	}

	/**
	 * Extends the current tap-navigation lock window.
	 */
	extendTapNavigationGuard(guardWindowMs: number = 700): void {
		const now = Date.now();
		const safeWindowMs = Math.max(0, guardWindowMs);
		this.tapNavigationBlockedUntilMs = Math.max(this.tapNavigationBlockedUntilMs, now + safeWindowMs);
		this.armGhostTapShield(safeWindowMs);
	}

	private armGhostTapShield(windowMs: number): void {
		if (typeof document === "undefined") {
			return;
		}

		const safeWindowMs = Math.max(0, windowMs);
		const now = Date.now();
		this.ghostTapShieldUntilMs = Math.max(this.ghostTapShieldUntilMs, now + safeWindowMs);

		if (!this.ghostTapShieldAttached) {
			for (const eventType of this.ghostTapShieldEventTypes) {
				document.addEventListener(eventType, this.ghostTapShieldHandler, true);
			}
			this.ghostTapShieldAttached = true;
		}

		const releaseDelayMs = safeWindowMs + 80;
		window.setTimeout(() => this.detachGhostTapShieldIfExpired(), releaseDelayMs);
	}

	private detachGhostTapShieldIfExpired(): void {
		if (!this.ghostTapShieldAttached) {
			return;
		}
		if (Date.now() <= this.ghostTapShieldUntilMs) {
			return;
		}

		if (typeof document !== "undefined") {
			for (const eventType of this.ghostTapShieldEventTypes) {
				document.removeEventListener(eventType, this.ghostTapShieldHandler, true);
			}
		}
		this.ghostTapShieldAttached = false;
	}

	private toDebugValue(value: any): any {
		if (value == null) {
			return value;
		}

		try {
			return JSON.parse(JSON.stringify(value));
		} catch {
			return String(value);
		}
	}

	GetQuerystringParameterByName(name: string) {
		name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
		const regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
			results = regex.exec(location.search);
		return results === null
			? ''
			: decodeURIComponent(results[1].replace(/\+/g, ' '));
	}

	ReplaceItemInArrayByIdOnlyIfPresent(arr: any, item: any) {
		let replaced = false;
		for (let i = 0; i < arr.length; i++) {
			if (arr[i].Id == item.Id) {
				arr[i] = item;
				replaced = true;
				break;
			}
		}
	}

	updateStringToSQLSyntax(originalString: string) {
		var newString = '';
		for (let counter = 0; counter < originalString.length; counter++) {
			var char = originalString[counter];

			switch (char) {
				case '\n':
					if (originalString[counter + 1] === '\n') {
						newString += '</p>\n<p>';
						counter++;
					} else newString += '<br />';
					break;

				case ' ':
					if (
						originalString[counter - 1] != ' ' &&
						originalString[counter - 1] != '\t'
					) {
						newString += ' ';
					}
					break;

				case "'":
					newString += "''";
					break;

				case '\t':
					if (originalString[counter - 1] != '\t') {
						newString += ' ';
					}
					break;

				case '&':
					newString += '&amp;';
					break;

				case '"':
					newString += '&quot;';
					break;

				case '>':
					newString += '&gt;';
					break;

				case '<':
					newString += '&lt;';
					break;

				default:
					newString += char;
			}
		}
		return newString;
	}

	ToFixed(number: number, fractionSize: number) {
		if (+number == 0) {
			return number;
		}
		var fixed = +(
			Math.round(+(number.toString() + 'e' + fractionSize)).toString() +
			'e' +
			-fractionSize
		);
		let fixedNumberAsString = fixed.toString();
		// console.log(fixedNumberAsString);
		if (fixedNumberAsString.indexOf('.') < 0) {
			fixedNumberAsString += '.0';
		}
		return fixedNumberAsString;
	}

	SecondsToString(seconds: any) {
		if (!seconds) {
			return '';
		}
		if (seconds == 'NULL') {
			return '';
		}
		if (seconds == 'null') {
			return '';
		}
		if (+seconds != seconds) {
			return '';
		}

		var secondsAsNumber = +seconds;
		let numdays = Math.floor(secondsAsNumber / 86400);
		var numDaysAsString = numdays.toString();
		numdays = parseInt(numDaysAsString);
		var numhours = Math.floor((secondsAsNumber % 86400) / 3600);
		var numminutes = Math.floor(((secondsAsNumber % 86400) % 3600) / 60);
		let numseconds = ((secondsAsNumber % 86400) % 3600) % 60;
		var numSecondsAsString = numseconds.toString();
		numseconds = parseInt(numSecondsAsString);

		// return numdays.toString();
		return (
			(numdays > 0 ? numdays + (numdays == 1 ? ' day ' : ' days ') : '') +
			(numhours > 9 ? numhours : '0' + numhours) +
			':' +
			(numminutes > 9 ? numminutes : '0' + numminutes) +
			':' +
			(numseconds > 9 ? numseconds : '0' + numseconds)
		);
	}

	translateMomentDateToJavascriptDate(momentDate: any) {
		var dateValue = momentDate._d;

		// var returnDate = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds(), dateValue.getMilliseconds());
		return dateValue;
	}

	FormatNumberWithCommas(x: any) {
		var parts = x.toString().split('.');
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return parts.join('.');
	}

	ConvertDateTimeToUTCDateTimeAndFormatForSQLParameter(inputDate: any) {
		var newDate = moment(inputDate).add(this.timeZoneOffsetHoursFromUTC, 'hours').format("MM/DD/YYYY HH:mm:ss");
		return newDate;
	}

	GetUTCQueryDate(inputDate: any) {
		// var newDate = moment(inputDate).add(this.timeZoneOffsetHoursFromUTC, 'hours');
		// return translateMomentDateToJavascriptDate(newDate);
		var newDate = moment(inputDate);
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetUTCQueryDateMinutesAgo(inputDate: any) {
		// var newDate = moment(inputDate).add(this.timeZoneOffsetHoursFromUTC, 'hours');
		// return translateMomentDateToJavascriptDate(newDate);
		var newDate = moment(inputDate);
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetODataQueryDateFromUTCDate(inputDate: any) {
		var newDate = moment(inputDate).subtract(
			this.timeZoneOffsetHoursFromUTC,
			'hours'
		);
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetODataQueryDateFromInputDate(inputDate: any) {
		var newDate = moment(inputDate);
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	DateTimeInMilliseconds(dt: any) {
		// returns date/time in milliseconds
		var inputDate = new Date(dt);
		return inputDate.getTime();
	}

	JavascriptDateTimeInMilliseconds(dateValue: any) {
		var jsDate = new Date(dateValue);
		var utcDate = new Date(
			jsDate.getUTCFullYear(),
			jsDate.getUTCMonth(),
			jsDate.getUTCDate(),
			jsDate.getUTCHours(),
			jsDate.getUTCMinutes(),
			jsDate.getUTCSeconds(),
			jsDate.getUTCMilliseconds()
		);
		return utcDate.getTime(); //-- returns date/time in milliseconds.
	}

	GetNonUTCQueryDate(inputDate: any) {
		var newDate = moment(inputDate);
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetQueryDateForNow() {
		var newDate = moment().add(this.timeZoneOffsetHoursFromUTC, 'hours');
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetQueryDateSecondsAgo(secondsToSubtract: number) {
		var newDate = moment()
			.add(this.timeZoneOffsetHoursFromUTC, 'hours')
			.subtract(secondsToSubtract, 'seconds');
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetQueryDateMinutesAgo(minutesToSubtract: number) {
		var newDate = moment()
			.add(this.timeZoneOffsetHoursFromUTC, 'hours')
			.subtract(minutesToSubtract, 'minutes');
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetQueryDateHoursAgo(hoursToSubtract: number) {
		var newDate = moment()
			.add(this.timeZoneOffsetHoursFromUTC, 'hours')
			.subtract(hoursToSubtract, 'hours');
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetQueryDateDaysAgo(daysToSubtract: number) {
		var newDate = moment()
			.add(this.timeZoneOffsetHoursFromUTC, 'hours')
			.subtract(daysToSubtract, 'days');
		return this.translateMomentDateToJavascriptDate(newDate);
	}

	GetOdataQueryDateForMidnightLocalTime() {
		var today = new Date();
		var myToday = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate(),
			0,
			0,
			0
		);
		var localUTCMidnight = moment(myToday).add(
			this.timeZoneOffsetHoursFromUTC * 2,
			'hours'
		);
		console.log(
			'LocalUTCMidnight = ' +
				this.translateMomentDateToJavascriptDate(localUTCMidnight)
		);
		return this.translateMomentDateToJavascriptDate(localUTCMidnight);
	}

	GetGateNameSortValue(gateName: any) {
		if (gateName) {
			if (typeof gateName.substring == 'function') {
				// if (!isFinite(gateName.substring(0, 1)) && isFinite(gateName.substring(1, 50))) {
				//    console.log("gateName = " + gateName + ", !isFinite(gateName.substring(0, 1)) = " + !isFinite(gateName.substring(0, 1)) + ", isFinite(gateName.substring(1, 50)) = " + isFinite(gateName.substring(1, 50)) + ", gateName.substring(0,1) = " + gateName.substring(0, 1) + ", gateName.substring(1, 50) = " + gateName.substring(1, 50));
				// }
				// if (!isFinite(gateName.substring(0, 1)) && !isFinite(gateName.substring(2, 3))) {
				//    console.log("gateName = " + gateName + ", !isFinite(gateName.substring(0, 1)) = " + !isFinite(gateName.substring(0, 1)) + ", !isFinite(gateName.substring(2, 3)) = " + !isFinite(gateName.substring(2, 3)) + ", gateName.substring(0,1) = " + gateName.substring(0, 1) + ", gateName.substring(2, 3) = " + gateName.substring(2, 3));
				// }
				// if (!isFinite(gateName.substring(0, 1)) && !isFinite(gateName.substring(3, 4))) {
				//    console.log("gateName = " + gateName + ", !isFinite(gateName.substring(0, 1)) = " + !isFinite(gateName.substring(0, 1)) + ", !isFinite(gateName.substring(3, 4)) = " + !isFinite(gateName.substring(3, 4)) + ", gateName.substring(0,1) = " + gateName.substring(0, 1) + ", gateName.substring(3, 4) = " + gateName.substring(3, 4));
				// }
				return !isFinite(gateName.substring(0, 1)) &&
					isFinite(gateName.substring(1, 50))
					? gateName.substring(0, 1) +
							gateName.substring(1, 50).padStart(4, '0')
					: !isFinite(gateName.substring(0, 1)) &&
					  !isFinite(gateName.substring(2, 3)) &&
					  isFinite(gateName.substring(3, 50))
					? gateName.substring(0, 1) +
					  gateName.substring(1, 2).padStart(4, '0') +
					  gateName.substring(2, 3) +
					  gateName.substring(3, 50).padStart(4, '0') // --IAH
					: !isFinite(gateName.substring(0, 1)) &&
					  !isFinite(gateName.substring(3, 4)) &&
					  isFinite(gateName.substring(4, 50))
					? gateName.substring(0, 1) +
					  gateName.substring(1, 3).padStart(4, '0') +
					  gateName.substring(3, 4) +
					  gateName.substring(4, 50).padStart(4, '0') // --JFK
					: isFinite(gateName.substring(0, 2)) &&
					  !isFinite(gateName.substring(2, 3)) &&
					  isFinite(gateName.substring(3, 50))
					? gateName.substring(0, 2).padStart(4, '0') +
					  gateName.substring(2, 3) +
					  gateName.substring(4, 50).padStart(4, '0') // --LAX
					: gateName.padStart(4, '0');
			} else {
				if (!isNaN(gateName) == true) {
					return +gateName;
				} else {
					return '';
				}
			}
		} else {
			return '';
		}
	}

	GetOdataUpdateableCurrentDateTime() {
		var utc = new Date().toISOString();
		console.log('utc = %O', utc);
		return utc;
	}

	GetOdataUpdateableCurrentDateTimeInUTCMilliseconds() {
		var utc = new Date().toISOString();
		var currentDateTimeInMilliseconds = this.DateTimeInMilliseconds(utc);
		return currentDateTimeInMilliseconds;
	}

	FormatDateToISOString(datestring: string) {
		var utc = new Date(datestring).toISOString();
		console.log('utc = %O', utc);
		return utc;
	}

	Camelize(str: string) {
		if (str) {
			return str.replace(
				/(?:^\w|[A-Z]|\b\w|\s+)/g,
				function (match, index) {
					if (+match === 0) {
						return '';
					} // or if (/\s+/.test(match)) for white spaces
					return index == 0
						? match.toLowerCase()
						: match.toUpperCase();
				}
			);
		}
		else {
			return null;
		}
	}

	ToPascalCase(str: string) {
		return str.replace(
			/\w\S*/g,
			(m) => m.charAt(0).toUpperCase() + m.substr(1).toLowerCase()
		);
	}

	GetJsonFromSignalR(signalRObject: any): any {
		var returnObject:any = {};
		var selectObject = {};
		// Accumulate the properties of the output object
		// Remove the leading Conn=xxx,
		var signalRString = signalRObject;
		if (signalRObject.substring(0,4) == "Conn=") {
			signalRString = signalRObject.substring(
				signalRObject.indexOf(',') + 1
			);
		}

		var splitSignalRString: any = signalRString.split('~');
		//	Global.User.DebugMode && console.log("splitSignalRString = %O", splitSignalRString);

		// for (var property in splitSignalRString) {

		splitSignalRString.forEach((property: any) => {
			var splitField:any = property.split('!');
			//Global.User.DebugMode && console.log("GetJsonFromSignalR splitField = %O", splitField);
			//Global.User.DebugMode && console.log("GetJsonFromSignalR splitField[0] = " + splitField[0] + ", splitField[1] = " + splitField[1]);
			returnObject[splitField[0]] = isNaN(+splitField[1])
				? splitField[1]
				: +splitField[1];
		});

		//Global.User.DebugMode && console.log("GetJsonFromSignalR returnObject = %O", returnObject);

		return returnObject;
	}

	convertFromUtcToLocalToSite(ms:any, offsetTarget:any, returnAsMS?:any) {
		if (offsetTarget !== 0 && !offsetTarget) return null;

		let offsetTargetInMin = offsetTarget * 60;
		var date = new Date(ms); //    date.setHours(offset);
		var offset = new Date().getTimezoneOffset();

		// let isDST = this.is_DST_Used_In_This_TimeZone();
		let isDST = false;
		let offsetToApplyToThisTimezone: any;
		let calculatedOffsetSoFar = offsetTargetInMin + offset;
		if (isDST) {
			calculatedOffsetSoFar = calculatedOffsetSoFar + 60;
		}
		offsetToApplyToThisTimezone = calculatedOffsetSoFar / 60;
		let finalDateInMs = date.setHours(
			date.getHours() + offsetToApplyToThisTimezone
		);
		if (returnAsMS === true) {
			return finalDateInMs;
		} else {
			return new Date(finalDateInMs);
		}
	}

	convertMillisecondsToDateWithTimezoneOffset(milliseconds: number, timezoneOffset: number) : any {
		if (!isNaN(milliseconds) && !isNaN(timezoneOffset)) {
			if (timezoneOffset !== 0 && !timezoneOffset) return null;

			let offsetTargetInMin = timezoneOffset * 60;
			var date = new Date(milliseconds); //    date.setHours(offset);
			var offset = new Date().getTimezoneOffset();

			let offsetToApplyToThisTimezone: any;
			let calculatedOffsetSoFar = offsetTargetInMin + offset;
			offsetToApplyToThisTimezone = calculatedOffsetSoFar / 60;

			let finalDateInMs = date.setHours(
				date.getHours() + offsetToApplyToThisTimezone
			);
			return new Date(finalDateInMs);
		}
		else {
			return null;
		}

	}

	convertDateToMillisecondsWithTimezoneOffset(requestedDate: Date, timezoneOffset: number) : any {
		if (timezoneOffset !== 0 && !timezoneOffset) return null;

		let offsetTargetInMin = timezoneOffset * 60;
		var date = new Date(requestedDate); //    date.setHours(offset);
		var offset = new Date().getTimezoneOffset();

		let offsetToApplyToThisTimezone: any;
		let calculatedOffsetSoFar = offsetTargetInMin + offset;
		offsetToApplyToThisTimezone = calculatedOffsetSoFar / 60;

		let finalDateInMs = date.setHours(
			date.getHours() + offsetToApplyToThisTimezone
		);
		return this.DateTimeInMilliseconds(new Date(finalDateInMs));
	}

	is_DST_Used_In_This_TimeZone() {
		let today = new Date(); //Create a date object that is now
		let thisYear = today.getFullYear(); //Get the year as a number
		let Jan_Date = new Date(thisYear, 0, 1); //Month is zero indexed - Jan is zero
		let jan_Timezone_OffSet = Jan_Date.getTimezoneOffset();
		let July_Date = new Date(thisYear, 6, 1);
		let july_Timezone_OffSet = July_Date.getTimezoneOffset();
		let offsetsNotEqual = july_Timezone_OffSet !== jan_Timezone_OffSet; //True if not equal

		return offsetsNotEqual; //If the offsets are not equal for summer and winter then the only possible reason is that DST is used for this time zone
	}

	GetUTCMSForNow() {
		return Date.now(); //-- go to this site to see why this changed: https://stackoverflow.com/questions/8047616/get-a-utc-timestamp
	}

	GetUTCDateFromLocalDate(localDate: any) {
		var timeZoneOffsetHoursFromUTC = new Date().getTimezoneOffset() / 60;
		if (localDate) {
			//console.log("================================================");
			//console.log("localDate = %O", localDate);

			var functionLocalDate = new Date(localDate);
			functionLocalDate.setHours(
				functionLocalDate.getHours() + timeZoneOffsetHoursFromUTC
			);
			//console.log("UTC Date Equivalent = %O", functionLocalDate);

			return functionLocalDate;
		}
		else {
			return null;
		}
	}

	GetLocalDateFromUTCDateString(utcDate: any) {
		var timeZoneOffsetHoursFromUTC = new Date().getTimezoneOffset() / 60;
		if (utcDate) {
			//console.log("================================================");
			//console.log("localDate = %O", localDate);
			var functionLocalDate = new Date({ ...utcDate });

			functionLocalDate.setHours(
				functionLocalDate.getHours() - timeZoneOffsetHoursFromUTC
			);
			//console.log("UTC Date Equivalent = %O", functionLocalDate);

			return functionLocalDate;
		}
		else {
			return null;
		}

	}

	GetLocalDateFromUTCDate(utcDate: any) {
		var timeZoneOffsetHoursFromUTC = new Date().getTimezoneOffset() / 60;
		if (utcDate && utcDate != '') {
			//console.log("================================================");
			//console.log("UTCDate = %O", utcDate);
			var newDate = moment(utcDate).subtract(
				timeZoneOffsetHoursFromUTC,
				'hours'
			);
			var localDate = this.translateMomentDateToJavascriptDate(newDate);
			//console.log("Local Date = %O", localDate);
			return localDate;
		}
		return null;
	}

	async waitUntilFinished(seconds: number) {
		setTimeout(() => {}, seconds);
		return true;
	}

	async showToastMessageShared(
		applicationSharedNotification: applicationSharedNotification
	) {
		console.log(
			'attempting to show toaster message: ' +
				applicationSharedNotification.message +
				'. Global.isMobile = ' +
				Global.isMobile
		);
		
		const toast = await this.mobileToastr.create({
			message: applicationSharedNotification.message,
			duration: 2000,
			position: 'bottom',
			cssClass: 'jbt-toaster-' + applicationSharedNotification.type,
		});
		toast.present();
		console.log('toastr message: ' + applicationSharedNotification.message);
	}

	async showAlert(htmlMessage: string, title?: string) {
		if (Global.isMobile) {
			window && window?.event?.stopPropagation();
			const alert = await this.mobileAlertify.create({
				message: htmlMessage,
				buttons: ['OK'],
			});
			alert.present();
		} else {
			const alert = await this.mobileAlertify.create({
				message: htmlMessage,
				header: title ? title : 'Alert'
			});
			alert.present();
		}
	}

	async errorNotificationAlert(errorMessage: string, title?: string) {
		console.log("errorNotificationAlert invoked. Error message: " + errorMessage + ", title: " + title);
		const alert = await this.mobileAlertify.create({
		header: title,
		message: errorMessage,
		buttons: [
			{
				text: 'OK',
				handler: () => {
					
					console.log("Error message displayed.");

				},
			},
		],
		});

		await alert.present();

	}

	GetUserTimeZoneOffsetHours() {
		return this.timeZoneOffsetHoursFromUTC;
	}

	isArray(value: any) {
		return value &&
			typeof value === 'object' &&
			typeof value.length === 'number' &&
			typeof value.splice === 'function' &&
			!(value.propertyIsEnumerable('length'));
	};

	/**
	 * Determines whether an asset is a camera asset.
	 */
	isCameraAsset(asset: any): boolean {
		if (!asset || typeof asset !== 'object') return false;
		if (asset.IsCameraAsset === true) return true;

		const name = String(asset.Name ?? asset.PreferredName ?? '').trim().toLowerCase();
		if (name.includes('camera')) return true;

		const assetTypeName = String(asset.AssetTypeName ?? asset.AssetType?.Name ?? '').trim().toLowerCase();
		if (assetTypeName.includes('camera')) return true;

		if (!!(asset.liveURL || asset.PlaylistUrl || asset.trustedSourceURL || asset.TrustedSourceURL)) return true;

		return false;
	}

	/**
	 * Filters a list of assets to return only non-camera assets.
	 */
	filterNonCameraAssets<T = any>(assets: T[] | undefined | null): T[] {
		if (!Array.isArray(assets)) return [];
		return assets.filter(asset => !this.isCameraAsset(asset));
	}
}

